/**
 * FlorEasy — devis questionnaire liés au compte client.
 *
 * - localStorage : cache immédiat + secours hors connexion / hors FieldsRaven
 * - FieldsRaven : écriture du metafield client floreasy.devis_list (JSON)
 * - seed Liquid : lecture du metafield au rendu de page
 */
(function (window) {
  'use strict';

  var PREFIX = 'floreasy_devis_list_';
  var EMAIL_PREFIX = 'floreasy_devis_list_email_';
  var MAX_ITEMS = 20;

  function storageKey(customerId) {
    return PREFIX + String(customerId || 'guest');
  }

  function emailStorageKey(email) {
    var normalized = String(email || '').trim().toLowerCase();
    if (!normalized) return '';
    return EMAIL_PREFIX + normalized;
  }

  function safeParse(raw) {
    try {
      var data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function readKey(key) {
    if (!key) return [];
    try {
      return safeParse(localStorage.getItem(key));
    } catch (e) {
      return [];
    }
  }

  function writeKey(key, items) {
    if (!key) return false;
    try {
      localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_ITEMS)));
      return true;
    } catch (e) {
      console.warn('[FlorEasyDevisStore] Écriture impossible:', e);
      return false;
    }
  }

  function clearKey(key) {
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  }

  function list(customerId) {
    if (!customerId) return [];
    return readKey(storageKey(customerId));
  }

  function write(customerId, items) {
    return writeKey(storageKey(customerId), items);
  }

  function get(customerId, devisId) {
    return list(customerId).find(function (item) {
      return item && String(item.id) === String(devisId);
    }) || null;
  }

  function fingerprint(devis) {
    if (!devis) return '';
    return [
      devis.univers || '',
      devis.budgetTotal || '',
      (devis.snapshot && devis.snapshot.weddingDate) || '',
      (devis.snapshot && devis.snapshot.email) || ''
    ].join('|').toLowerCase();
  }

  function upsertInto(items, devis) {
    var fp = fingerprint(devis);
    var now = Date.now();
    var replaced = false;

    for (var i = 0; i < items.length; i++) {
      if (fingerprint(items[i]) === fp && (now - (items[i].createdAt || 0)) < 86400000) {
        devis.id = items[i].id;
        devis.createdAt = items[i].createdAt || devis.createdAt || now;
        items[i] = devis;
        replaced = true;
        break;
      }
    }

    if (!replaced) {
      if (!devis.id) {
        devis.id = 'devis_' + now + '_' + Math.random().toString(36).slice(2, 8);
      }
      if (!devis.createdAt) devis.createdAt = now;
      items.unshift(devis);
    }

    return items.slice(0, MAX_ITEMS);
  }

  function save(customerId, devis) {
    if (!customerId || !devis) return null;
    var items = upsertInto(list(customerId), devis);
    write(customerId, items);
    return devis;
  }

  function saveByEmail(email, devis) {
    var key = emailStorageKey(email);
    if (!key || !devis) return null;
    var items = upsertInto(readKey(key), devis);
    writeKey(key, items);
    return devis;
  }

  function adoptByEmail(customerId, email) {
    if (!customerId || !email) return list(customerId);
    var key = emailStorageKey(email);
    var pending = readKey(key);
    if (!pending.length) return list(customerId);

    var items = list(customerId);
    pending.forEach(function (devis) {
      items = upsertInto(items, devis);
    });
    write(customerId, items);
    clearKey(key);
    return items;
  }

  function remove(customerId, devisId) {
    if (!customerId || !devisId) return false;
    var next = list(customerId).filter(function (item) {
      return item && String(item.id) !== String(devisId);
    });
    return write(customerId, next);
  }

  /* ------------------------------------------------ FieldsRaven sync ---- */

  function getConfig() {
    return window.FlorEasyDevisRavenConfig || {};
  }

  function seedFromConfig() {
    return safeParse(getConfig().seed);
  }

  function isRavenReady() {
    var cfg = getConfig();
    return !!(
      cfg.enabled &&
      cfg.auth &&
      cfg.auth.raven_id &&
      cfg.auth.raven_id !== 'TBD' &&
      cfg.auth.raven_mac &&
      cfg.auth.resource_id &&
      cfg.customerId
    );
  }

  function mergeLists() {
    var seen = {};
    var result = [];
    for (var i = 0; i < arguments.length; i++) {
      var items = Array.isArray(arguments[i]) ? arguments[i] : [];
      for (var j = 0; j < items.length; j++) {
        var item = items[j];
        if (!item || !item.id) continue;
        var id = String(item.id);
        if (seen[id]) continue;
        seen[id] = true;
        result.push(item);
      }
    }
    return result
      .sort(function (a, b) {
        return (b.createdAt || 0) - (a.createdAt || 0);
      })
      .slice(0, MAX_ITEMS);
  }

  /**
   * Pousse la liste complète vers le metafield via FieldsRaven.
   * Format exact de la doc : PUT /apps/raven/create_metafield
   * body = { raven: { raven_id, resource_id, raven_mac, value } }
   */
  function pushToRaven(items) {
    if (!isRavenReady()) {
      return Promise.resolve({ synced: false, reason: 'raven_not_ready' });
    }

    var cfg = getConfig();
    var auth = cfg.auth || {};
    var ravenId = String(auth.raven_id || cfg.ravenId || '').trim();
    var resourceId = String(auth.resource_id || cfg.customerId || '').trim();
    var ravenMac = String(auth.raven_mac || '').trim();

    if (!ravenId || ravenId === 'TBD' || !resourceId || !ravenMac) {
      console.warn('[FlorEasyDevisStore] Config Raven incomplète:', {
        ravenId: ravenId,
        resourceId: resourceId,
        hasMac: !!ravenMac
      });
      return Promise.resolve({ synced: false, reason: 'incomplete_auth' });
    }

    var payload = {
      raven: {
        raven_id: ravenId,
        resource_id: resourceId,
        raven_mac: ravenMac,
        value: JSON.stringify(items || [])
      }
    };

    // Toujours fetch natif (évite un double-wrap du Storefront Kit).
    return fetch(cfg.endpoint || '/apps/raven/create_metafield', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { status: response.status, json: json };
        }).catch(function () {
          return { status: response.status, json: null };
        });
      })
      .then(function (res) {
        var ok = res && res.status === 200;
        if (!ok) {
          console.warn('[FlorEasyDevisStore] FieldsRaven:', res, 'payload.raven_id=', ravenId);
          return { synced: false, reason: 'raven_error', response: res };
        }
        return { synced: true };
      })
      .catch(function (err) {
        console.warn('[FlorEasyDevisStore] FieldsRaven réseau:', err);
        return { synced: false, reason: 'network' };
      });
  }

  /**
   * Charge devis : seed metafield + local + e-mail temporaire, puis pousse si besoin.
   */
  function load(customerId, email) {
    var localItems = list(customerId);
    var pendingKey = emailStorageKey(email);
    var pendingItems = readKey(pendingKey);
    var remoteSeed = seedFromConfig();
    var merged = mergeLists(remoteSeed, localItems, pendingItems);

    if (customerId) write(customerId, merged);
    if (pendingItems.length) clearKey(pendingKey);

    if (!isRavenReady() || !customerId) {
      return Promise.resolve({ items: merged, synced: false });
    }

    // Si le local a plus (ou différent) que le seed, on pousse la fusion.
    var seedSig = remoteSeed.map(function (i) { return i && i.id; }).join(',');
    var mergedSig = merged.map(function (i) { return i && i.id; }).join(',');
    var shouldPush = mergedSig !== seedSig;

    if (!shouldPush) {
      return Promise.resolve({ items: merged, synced: true });
    }

    return pushToRaven(merged).then(function (result) {
      return { items: merged, synced: !!(result && result.synced) };
    });
  }

  function saveSynced(customerId, email, devis) {
    if (!devis) return Promise.resolve({ devis: null, synced: false });

    var record = null;
    if (customerId) record = save(customerId, devis);
    else if (email) record = saveByEmail(email, devis);
    if (!record) record = devis;

    if (!customerId || !isRavenReady()) {
      return Promise.resolve({ devis: record, synced: false });
    }

    return pushToRaven(list(customerId)).then(function (result) {
      return { devis: record, synced: !!(result && result.synced) };
    });
  }

  function removeSynced(customerId, devisId) {
    remove(customerId, devisId);
    var items = list(customerId);

    if (!customerId || !isRavenReady()) {
      return Promise.resolve({ items: items, synced: false });
    }

    return pushToRaven(items).then(function (result) {
      return { items: items, synced: !!(result && result.synced) };
    });
  }

  /* ------------------------------------ Inbox site (tous les devis) ---- */

  var SITE_KEY = 'floreasy_devis_list_site';
  var MAX_SITE_ITEMS = 80;

  function getSiteConfig() {
    return getConfig().siteInbox || {};
  }

  function isSiteRavenReady() {
    var site = getSiteConfig();
    return !!(
      site.enabled &&
      site.auth &&
      site.auth.raven_id &&
      site.auth.raven_id !== 'TBD' &&
      site.auth.raven_mac &&
      site.auth.resource_id
    );
  }

  function listSite() {
    return readKey(SITE_KEY);
  }

  function seedSiteFromConfig() {
    return safeParse(getSiteConfig().seed);
  }

  function toSiteInboxRecord(devis) {
    if (!devis) return null;
    var snapshot = devis.snapshot || {};
    return {
      id: devis.id,
      createdAt: devis.createdAt || Date.now(),
      createdAtIso: new Date(devis.createdAt || Date.now()).toISOString(),
      source: 'site_questionnaire',
      loggedIn: !!devis.loggedIn,
      customerId: devis.customerId || null,
      univers: devis.univers || '',
      budgetTotal: devis.budgetTotal || '',
      totalFlowers: devis.totalFlowers || '',
      duration: devis.duration || '',
      participants: devis.participants || snapshot.participants || '',
      message: devis.message || '',
      answersSummary: Array.isArray(devis.answersSummary) ? devis.answersSummary : [],
      snapshot: {
        firstName: snapshot.firstName || '',
        lastName: snapshot.lastName || '',
        email: snapshot.email || '',
        phone: snapshot.phone || '',
        weddingDate: snapshot.weddingDate || '',
        postalCode: snapshot.postalCode || '',
        participants: snapshot.participants || '',
        pack: snapshot.pack || null,
        freshItems: Array.isArray(snapshot.freshItems) ? snapshot.freshItems : [],
        supplements: Array.isArray(snapshot.supplements) ? snapshot.supplements : []
      }
    };
  }

  function pushSiteToRaven(items) {
    if (!isSiteRavenReady()) {
      return Promise.resolve({ synced: false, reason: 'site_raven_not_ready' });
    }

    var site = getSiteConfig();
    var auth = site.auth || {};
    var ravenId = String(auth.raven_id || site.ravenId || '').trim();
    var resourceId = String(auth.resource_id || site.pageId || '').trim();
    var ravenMac = String(auth.raven_mac || '').trim();

    if (!ravenId || ravenId === 'TBD' || !resourceId || !ravenMac) {
      console.warn('[FlorEasyDevisStore] Config Raven site incomplète:', {
        ravenId: ravenId,
        resourceId: resourceId,
        hasMac: !!ravenMac
      });
      return Promise.resolve({ synced: false, reason: 'incomplete_site_auth' });
    }

    var payload = {
      raven: {
        raven_id: ravenId,
        resource_id: resourceId,
        raven_mac: ravenMac,
        value: JSON.stringify((items || []).slice(0, MAX_SITE_ITEMS))
      }
    };

    return fetch(site.endpoint || '/apps/raven/create_metafield', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (json) {
          return { status: response.status, json: json };
        }).catch(function () {
          return { status: response.status, json: null };
        });
      })
      .then(function (res) {
        var ok = res && res.status === 200;
        if (!ok) {
          console.warn('[FlorEasyDevisStore] FieldsRaven site:', res, 'payload.raven_id=', ravenId);
          return { synced: false, reason: 'raven_error', response: res };
        }
        return { synced: true };
      })
      .catch(function (err) {
        console.warn('[FlorEasyDevisStore] FieldsRaven site réseau:', err);
        return { synced: false, reason: 'network' };
      });
  }

  function saveSiteSynced(devis) {
    var record = toSiteInboxRecord(devis);
    if (!record) return Promise.resolve({ devis: null, synced: false });

    var items = upsertInto(mergeLists(seedSiteFromConfig(), listSite()), record);
    items = items.slice(0, MAX_SITE_ITEMS);
    writeKey(SITE_KEY, items);

    if (!isSiteRavenReady()) {
      return Promise.resolve({ devis: record, synced: false, reason: 'site_raven_not_ready' });
    }

    return pushSiteToRaven(items).then(function (result) {
      return { devis: record, synced: !!(result && result.synced) };
    });
  }

  window.FlorEasyDevisStore = {
    list: list,
    get: get,
    save: save,
    saveByEmail: saveByEmail,
    adoptByEmail: adoptByEmail,
    remove: remove,
    load: load,
    saveSynced: saveSynced,
    removeSynced: removeSynced,
    saveSiteSynced: saveSiteSynced,
    isRavenReady: isRavenReady,
    isSiteRavenReady: isSiteRavenReady,
    fingerprint: fingerprint,
    storageKey: storageKey
  };
})(window);
